import React from "react";
import { Alert, AlertIcon, Spinner, VStack } from "@chakra-ui/react";
import { useQuery } from "@tanstack/react-query";
import { CrudPage } from "../components/CrudPage";
import { useAuth } from "../context/AuthContext";
import { tadbir } from "../services/api";
import {
  getUserIdentifier,
  getUserIdentifierLabel,
} from "../utils/userIdentity";

const pageSize = 20;

const formatNumber = (value: number) => value.toLocaleString("fa-IR");

const columns = [
  {
    header: "نام صندوق",
    accessor: "fundName",
    render: (item: any) => item.fundName?.trim() || "-",
  },
  {
    header: "نوع صندوق",
    accessor: "fundTypeTitle",
    render: (item: any) => item.fundTypeTitle?.trim() || "-",
  },
  { header: "کد ثبت صندوق", accessor: "fundSeoRegisterNo" },
  {
    header: "واحد باقی‌مانده",
    accessor: "remainingUnit",
    render: (item: any) => formatNumber(item.remainingUnit),
  },
  {
    header: "تعداد ضمانت",
    accessor: "guaranteeCount",
    render: (item: any) => formatNumber(item.guaranteeCount),
  },
  {
    header: "باقی‌مانده با ضمانت و درخواست",
    accessor: "remainingWithGuaranteeAndRequest",
    render: (item: any) => formatNumber(item.remainingWithGuaranteeAndRequest),
  },
  {
    header: "درخواست ابطال",
    accessor: "requestForCancellationCount",
    render: (item: any) => formatNumber(item.requestForCancellationCount),
  },
  {
    header: "ابطال در انتظار",
    accessor: "cancelRequestInWaitCount",
    render: (item: any) => formatNumber(item.cancelRequestInWaitCount),
  },
];

export const BalancePage: React.FC = () => {
  const { user } = useAuth();
  const identifier = getUserIdentifier(user);
  const identifierLabel = getUserIdentifierLabel(user);

  const { isLoading, isError } = useQuery(
    ["omidib-balances", identifier],
    () => tadbir.getBalances(),
    {
      enabled: !!identifier,
    },
  );

  if (!identifier) {
    return (
      <Alert status="warning" borderRadius="lg">
        <AlertIcon />
        {identifierLabel} کاربر برای دریافت موجودی‌ها یافت نشد.
      </Alert>
    );
  }

  if (isLoading) {
    return (
      <VStack w="100%" h="100%" justify="center" align="center">
        <Spinner size="xl" />
      </VStack>
    );
  }

  if (isError) {
    return (
      <Alert status="error" borderRadius="lg">
        <AlertIcon />
        دریافت موجودی‌ها با خطا مواجه شد.
      </Alert>
    );
  }

  return (
    <CrudPage
      title="موجودی"
      columns={columns}
      queryKey="balances"
      dataKey="balances"
      resourceName="balances"
      api={{
        getAll: async (page, size) => {
          const response = await tadbir.getBalances();
          const balances = response.balances || [];
          const totalData = balances.length;
          const totalPage = Math.max(1, Math.ceil(totalData / size));
          const slicedBalances = balances.slice((page - 1) * size, page * size);

          return {
            balances: slicedBalances,
            totalData,
            currentPage: page,
            totalPage,
          };
        },
      }}
      FormComponent={() => null}
      showAddButton={false}
      enableEdit={false}
      enableDelete={false}
      enableView={false}
      queryDependencies={[identifier]}
      queryEnabled={!!identifier}
      pageSize={pageSize}
    />
  );
};
